import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Plus, Calendar, Users, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSeo } from "@/lib/seo";

const THEME_LABEL = {
  celebration: "Celebration",
  farewell: "Farewell",
  wedding: "Wedding",
  birthday: "Birthday",
  retirement: "Retirement",
  memorial: "Memorial",
};

export default function Dashboard() {
  useSeo({ title: "Your events", noindex: true, path: "/dashboard" });
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/events").then(async (r) => {
      setEvents(r.data);
      const entries = await Promise.all(
        r.data.map((e) => api.get(`/events/${e.id}/submissions`).then((rs) => [e.id, rs.data.length]).catch(() => [e.id, 0]))
      );
      setCounts(Object.fromEntries(entries));
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--em-bg)]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-28 md:pb-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="font-mono-label text-[var(--em-clay)]">Your stories</p>
            <h1 className="font-serif-em text-4xl md:text-5xl mt-3">Dashboard</h1>
            <p className="mt-2 text-[var(--em-ink-soft)] max-w-xl">Every event is a film in the making — voices arrive, you arrange the story.</p>
          </div>
          <Button data-testid="dashboard-create-event-btn" onClick={() => navigate("/events/new")} className="h-11 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white px-6 btn-lift">
            <Plus className="mr-2 h-4 w-4" /> New event
          </Button>
        </div>

        <div className="mt-10">
          {loading ? (
            <p className="text-[var(--em-ink-soft)]">Loading your events…</p>
          ) : events.length === 0 ? (
            <div className="border border-dashed border-[var(--em-line)] rounded-2xl p-12 text-center bg-white" data-testid="dashboard-empty">
              <h3 className="font-serif-em text-2xl">No memories yet.</h3>
              <p className="mt-2 text-[var(--em-ink-soft)]">Start your first tribute — a goodbye, wedding, birthday, or any moment.</p>
              <Button data-testid="dashboard-empty-create-btn" onClick={() => navigate("/events/new")} className="mt-6 h-11 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white px-6 btn-lift">
                Create your first event
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((e, i) => (
                <motion.div key={e.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}>
                    <div className="soft-card bg-white border border-[var(--em-line)] rounded-2xl p-6 h-full flex flex-col">
                      <div className="flex items-center justify-between">
                        <span className="font-mono-label text-[var(--em-sage)]">{THEME_LABEL[e.theme] || e.theme}</span>
                        <ArrowUpRight className="text-[var(--em-ink-soft)]" size={18} />
                      </div>
                      <h3 className="font-serif-em text-2xl mt-4 leading-snug">{e.title}</h3>
                      <p className="text-sm text-[var(--em-ink-soft)] mt-2 line-clamp-2">{e.description || "Your memory in the making."}</p>
                      <div className="mt-6 flex items-center gap-5 text-sm text-[var(--em-ink-soft)]">
                        <span className="inline-flex items-center gap-1.5"><Users size={14} /> {counts[e.id] ?? 0} voices</span>
                        {e.deadline && <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> by {new Date(e.deadline).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
